import { IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { CompactEmptyState } from "./SectionLayout";
import { keywordKey, parseKeywordList } from "./sponsored-brands-format";
import { NEGATIVE_KEYWORD_MATCH_TYPES, type NegativeKeywordRow } from "./sponsored-brands-model";

export function NegativeKeywordTargeting({
  negativeInput,
  onNegativeInputChange,
  negativeKeywords,
  onNegativeKeywordsChange,
}: {
  negativeInput: string;
  onNegativeInputChange: (value: string) => void;
  negativeKeywords: NegativeKeywordRow[];
  onNegativeKeywordsChange: (value: NegativeKeywordRow[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const [negativeMatches, setNegativeMatches] = useState({
    negativePhrase: true,
    negativeExact: true,
  });

  const addNegativeKeywords = () => {
    const values = parseKeywordList(negativeInput);
    const existing = new Set(negativeKeywords.map((row) => keywordKey(row.matchType, row.keyword)));
    const next = [...negativeKeywords];
    for (const keyword of values) {
      for (const matchType of NEGATIVE_KEYWORD_MATCH_TYPES) {
        const key = keywordKey(matchType, keyword);
        if (!negativeMatches[matchType] || existing.has(key)) continue;
        existing.add(key);
        next.push({ id: crypto.randomUUID(), keyword, matchType });
      }
    }
    onNegativeKeywordsChange(next);
    onNegativeInputChange("");
  };

  return (
    <div>
      <h3 className="mb-3 font-semibold">{t("sb.negative.title")}</h3>
      <div
        data-slot="sb-negative-keyword-workspace"
        className="grid min-h-[420px] grid-cols-2 overflow-hidden rounded-lg border border-border"
      >
        <Tabs defaultValue="enter" className="min-w-0 p-4">
          <TabsList>
            <TabsTrigger value="enter">{t("sb.keyword.enter")}</TabsTrigger>
            <TabsTrigger value="library" disabled title={t("sb.blockers.keywordLibrary")}>
              {t("sb.keyword.library")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="enter" className="flex flex-col gap-3 pt-4">
            <FieldSet className="gap-0">
              <FieldLegend variant="label" className="sr-only">
                {t("sb.keyword.filter")}
              </FieldLegend>
              <div
                data-slot="sb-negative-keyword-match-row"
                className="flex flex-row items-center justify-between gap-4"
              >
                <span className="shrink-0 text-sm font-medium">{t("sb.keyword.filter")}</span>
                <div
                  data-slot="sb-negative-keyword-match-types"
                  className="ml-auto flex flex-nowrap gap-4"
                >
                  {NEGATIVE_KEYWORD_MATCH_TYPES.map((matchType) => (
                    <Field
                      key={matchType}
                      orientation="horizontal"
                      className="shrink-0 flex-nowrap items-center gap-1.5"
                    >
                      <Checkbox
                        checked={negativeMatches[matchType]}
                        onCheckedChange={(checked) =>
                          setNegativeMatches({
                            ...negativeMatches,
                            [matchType]: checked === true,
                          })
                        }
                      />
                      <FieldLabel className="whitespace-nowrap">
                        {t(`sb.negative.${matchType}`)}
                      </FieldLabel>
                    </Field>
                  ))}
                </div>
              </div>
            </FieldSet>
            <Textarea
              className="min-h-60"
              aria-label={t("sb.negative.list")}
              value={negativeInput}
              onChange={(event) => onNegativeInputChange(event.target.value)}
              placeholder={t("sb.keyword.placeholder")}
            />
            <p className="text-sm text-muted-foreground">{t("sb.keyword.maximum")}</p>
            <Button
              type="button"
              className="self-start"
              disabled={!negativeInput.trim() || !Object.values(negativeMatches).some(Boolean)}
              onClick={addNegativeKeywords}
            >
              {t("sb.actions.add")}
            </Button>
          </TabsContent>
          <TabsContent value="library" className="pt-4">
            <p className="text-sm text-muted-foreground">{t("sb.keyword.libraryEmpty")}</p>
          </TabsContent>
        </Tabs>
        <div className="min-w-0 border-l border-border p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-medium">
              {t("sb.common.added", { count: negativeKeywords.length })}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!negativeKeywords.length}
              onClick={() => onNegativeKeywordsChange([])}
            >
              {t("sb.common.removeAll")}
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("sb.negative.title")}</TableHead>
                <TableHead>{t("sb.common.action")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {negativeKeywords.length ? (
                negativeKeywords.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {t(`sb.negative.${row.matchType}`)} · {row.keyword}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("sb.common.remove", { name: row.keyword })}
                        onClick={() =>
                          onNegativeKeywordsChange(
                            negativeKeywords.filter((item) => item.id !== row.id),
                          )
                        }
                      >
                        <IconTrash />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2}>
                    <CompactEmptyState />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
